/**
 * Find, and Find & Replace (Ctrl+F / Ctrl+H).
 *
 * Replacements go through the same editor as typing, so they keep each
 * document's fonts and spacing; the summary says if any could not be made
 * and why, or if a longer replacement now runs into neighbouring text.
 */

import { useEffect, useRef, useState } from 'react'
import { api, type FindMatch, type ReplaceOutcome, type SessionInfo } from '../api'

interface Props {
  sessionId: string
  showReplace: boolean
  matches: FindMatch[]
  current: number
  onResults: (matches: FindMatch[], current: number) => void
  onReplaced: (session: SessionInfo, summary: { level: 'info' | 'warn'; title: string; body: string }) => void
  onClose: () => void
}

function summarise(replaced: number, results: ReplaceOutcome[]) {
  const failed = results.filter((r) => r.fidelity === 'failed')
  const added = results.flatMap((r) => r.additions.map((a) => a.char))
  const close = results.flatMap((r) => r.additions.filter((a) => !a.exact))
  const overlaps = results.filter((r) => r.overlaps.length)
  const parts: string[] = [`Replaced ${replaced}.`]
  if (added.length) parts.push(`Added ${[...new Set(added)].join(' ')} from the document’s own font${close.length ? ' (some as a close match)' : ''}.`)
  if (failed.length) {
    const letters = [...new Set(failed.flatMap((r) => r.missingGlyphs ?? []))].join(' ')
    parts.push(`${failed.length} could not be changed: no available font has ${letters}.`)
  }
  if (overlaps.length) parts.push(`${overlaps.length} now run into the text after them — check those lines.`)
  const level: 'info' | 'warn' = failed.length || overlaps.length || close.length ? 'warn' : 'info'
  return { level, title: replaced ? 'Replaced' : 'Nothing replaced', body: parts.join(' ') }
}

export function FindBar({ sessionId, showReplace, matches, current, onResults, onReplaced, onClose }: Props) {
  const [query, setQuery] = useState('')
  const [replacement, setReplacement] = useState('')
  const [matchCase, setMatchCase] = useState(false)
  const [wholeWord, setWholeWord] = useState(false)
  const [replacing, setReplacing] = useState(showReplace)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => { input.current?.focus(); input.current?.select() }, [])
  useEffect(() => { if (showReplace) setReplacing(true) }, [showReplace])

  // Search as you type, debounced.
  useEffect(() => {
    if (!query) {
      onResults([], 0)
      setError('')
      return
    }
    const timer = window.setTimeout(() => {
      void api.search(sessionId, query, matchCase, wholeWord)
        .then((r) => { setError(''); onResults(r.matches, 0) })
        .catch((err) => { setError(err.message); onResults([], 0) })
    }, 200)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, matchCase, wholeWord, sessionId])

  const step = (delta: number) => {
    if (!matches.length) return
    onResults(matches, (current + delta + matches.length) % matches.length)
  }

  const replace = async (only: boolean) => {
    if (!query) return
    setBusy(true)
    try {
      const target = matches[current]
      const r = await api.replace(sessionId, {
        query, replacement, matchCase, wholeWord,
        only: only && target
          ? [{ pageIndex: target.pageIndex, segmentId: target.segmentId, start: target.start }]
          : undefined,
      })
      onReplaced(r.session, summarise(r.replaced, r.results))
      const again = await api.search(sessionId, query, matchCase, wholeWord)
      onResults(again.matches, Math.min(current, Math.max(0, again.matches.length - 1)))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="findbar" role="search"
         onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onClose() } }}>
      <div className="findbar__row">
        <button className="findbar__toggle" aria-expanded={replacing}
                title={replacing ? 'Hide replace' : 'Replace'}
                onClick={() => setReplacing(!replacing)}>
          {replacing ? '▾' : '▸'}
        </button>
        <input ref={input} className="findbar__input" placeholder="Find" value={query}
               aria-label="Find"
               onChange={(e) => setQuery(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); step(e.shiftKey ? -1 : 1) } }} />
        <span className="findbar__count" aria-live="polite">
          {query ? (matches.length ? `${current + 1} of ${matches.length}` : 'No results') : ''}
        </span>
        <button className="findbar__btn" title="Previous (Shift+Enter)" disabled={!matches.length}
                onClick={() => step(-1)}>{'↑'}</button>
        <button className="findbar__btn" title="Next (Enter)" disabled={!matches.length}
                onClick={() => step(1)}>{'↓'}</button>
        <label className="findbar__opt" title="Match case">
          <input type="checkbox" checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)} /> Aa
        </label>
        <label className="findbar__opt" title="Whole word">
          <input type="checkbox" checked={wholeWord} onChange={(e) => setWholeWord(e.target.checked)} /> Word
        </label>
        <button className="findbar__btn" title="Close (Esc)" onClick={onClose}>{'✕'}</button>
      </div>
      {replacing && (
        <div className="findbar__row">
          <span className="findbar__toggle" aria-hidden="true" />
          <input className="findbar__input" placeholder="Replace with" value={replacement}
                 aria-label="Replace with"
                 onChange={(e) => setReplacement(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void replace(true) } }} />
          <button className="btn btn--small" disabled={busy || !matches.length}
                  onClick={() => void replace(true)}>Replace</button>
          <button className="btn btn--small" disabled={busy || !matches.length}
                  onClick={() => void replace(false)}>Replace all</button>
        </div>
      )}
      {replacing && !matchCase && replacement && (
        <div className="findbar__hint">Follows the case of each match, as Word does: {query.toUpperCase()} &rarr; {replacement.toUpperCase()}</div>
      )}
      {error && <div className="findbar__hint findbar__hint--warn">{error}</div>}
    </div>
  )
}
